import { Injectable, signal, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BROWSER_STORAGE } from '../platform/browser-storage';

/**
 * ThemeService — panelin açık/koyu teması.
 *
 * Öncelik: saklı seçim (`mypt-theme`) > sistem tercihi (prefers-color-scheme) > açık.
 * Tema <html data-theme> ile uygulanır; renkler styles/tokens.css'teki --mypt-*
 * belirteçlerinden gelir. Public site (.mypt-site) --site-* değerlerini kendi üstünde
 * yeniden tanımladığı için panelin koyu temasından etkilenmez.
 *
 * index.html'deki erken betik aynı önceliği Angular açılmadan uygular (yanlış tema
 * parlaması olmaz); bu servis sonra devralır. Depo erişimi gizli modda hata
 * verebilir — her okuma/yazma try/catch içinde.
 */
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'mypt-theme';

/** Koyu tema açık (Faz 5 Task 10). index.html erken betiği de buna göre okur. */
export const DARK_MODE_ENABLED = true;

/** Tarayıcı çubuğu rengi: temanın --mypt-bg değeri (styles/tokens.css; spec eşitliği ölçer).
 *  index.html'deki statik <meta name="theme-color"> açık değerle aynı. */
export const THEME_COLOR: Record<Theme, string> = { light: '#f5f8f7', dark: '#0e1514' };

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storage = inject(BROWSER_STORAGE);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Reaktif tema sinyali. */
  readonly theme = signal<Theme>(this.getInitialTheme());

  /** Panel kabuğu (AppShell) açık mı — koyu tarayıcı çubuğu yalnız o zaman; public site hep açık. */
  private readonly panelChrome = signal(false);

  constructor() {
    effect(() => this.applyTheme(this.theme()));
    effect(() => this.applyThemeColor(this.panelChrome() ? this.theme() : 'light'));

    // Kullanıcı açıkça seçmediyse sistem tercihini canlı izle.
    if (DARK_MODE_ENABLED && this.isBrowser && typeof window.matchMedia === 'function') {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', (e) => {
        if (!this.readStored()) this.theme.set(e.matches ? 'dark' : 'light');
      });
    }
  }

  /** Açık ↔ koyu; seçim saklanır. */
  toggle(): void {
    this.setTheme(this.theme() === 'light' ? 'dark' : 'light');
  }

  /** Belirli bir temayı seç ve sakla. */
  setTheme(theme: Theme): void {
    const effective: Theme = DARK_MODE_ENABLED ? theme : 'light';
    this.theme.set(effective);
    try {
      this.storage.setItem(THEME_STORAGE_KEY, effective);
    } catch {
      // Depo engelli (gizli mod / kota): seçim yalnız bu oturumda geçerli.
    }
  }

  /** AppShell açılınca true, kapanınca false verir. */
  setPanelChrome(active: boolean): void {
    this.panelChrome.set(active);
  }

  private readStored(): Theme | null {
    try {
      const stored = this.storage.getItem(THEME_STORAGE_KEY);
      return stored === 'dark' || stored === 'light' ? stored : null;
    } catch {
      return null;
    }
  }

  private getInitialTheme(): Theme {
    if (!DARK_MODE_ENABLED || !this.isBrowser) return 'light';
    const stored = this.readStored();
    if (stored) return stored;
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }

  private applyTheme(theme: Theme): void {
    if (!this.isBrowser) return;
    document.documentElement.setAttribute('data-theme', theme);
  }

  private applyThemeColor(theme: Theme): void {
    if (!this.isBrowser) return;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  }
}
